import { api } from ".";
import { IEdiConversionFile } from "../../types/ediConversionFile";

const ediConversionQueries = api.injectEndpoints({
  endpoints: (builder) => ({
    getAllEdiConversionFiles: builder.query<
      { data: IEdiConversionFile[]; message: string },
      { search?: string }
    >({
      query: (params) => ({
        url: "/edi-conversion",
        params,
      }),
      providesTags: ["EdiConversion"],
    }),
    getEdiConversionFileById: builder.query<
      { data: IEdiConversionFile; message: string },
      string
    >({
      query: (id) => `/edi-conversion/${id}`,
    }),
    convertAndUploadEdi: builder.mutation<
      { data: IEdiConversionFile; message: string; warnings?: string[] },
      {
        files: File[];
        displayName: string;
        description?: string;
        providerId?: string;
        files837?: File[];
        placeOfServiceFilter?: string[];
      }
    >({
      query: ({
        files,
        displayName,
        description,
        providerId,
        files837,
        placeOfServiceFilter,
      }) => {
        const formData = new FormData();
        files.forEach((f) => formData.append("files", f));
        if (files837) {
          files837.forEach((f) => formData.append("files837", f));
        }
        formData.append("displayName", displayName);
        if (description) {
          formData.append("description", description);
        }
        if (providerId) {
          formData.append("providerId", providerId);
        }
        if (placeOfServiceFilter && placeOfServiceFilter.length > 0) {
          formData.append(
            "placeOfServiceFilter",
            JSON.stringify(placeOfServiceFilter)
          );
        }

        return {
          url: "/edi-conversion/convert",
          method: "POST",
          body: formData,
        };
      },
      invalidatesTags: ["EdiConversion"],
    }),
    getEdiConversionFileDownloadUrl: builder.query<
      { url: string; message: string },
      string
    >({
      query: (id) => `/edi-conversion/${id}/download`,
    }),
    deleteEdiConversionFile: builder.mutation<{ message: string }, string>({
      query: (id) => ({
        url: `/edi-conversion/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["EdiConversion"],
    }),
    reprocessEdiFile: builder.mutation<
      { data: IEdiConversionFile; message: string },
      string
    >({
      query: (id) => ({
        url: `/edi-conversion/${id}/reprocess`,
        method: "POST",
      }),
      invalidatesTags: ["EdiConversion"],
    }),
    reprocessCptCoOccurrence: builder.mutation<
      { data: IEdiConversionFile; message: string },
      string
    >({
      query: (id) => ({
        url: `/edi-conversion/${id}/reprocess-cpt-co-occurrence`,
        method: "POST",
      }),
      invalidatesTags: ["EdiConversion"],
    }),
    reprocessImplantAssociations: builder.mutation<
      { data: IEdiConversionFile; message: string },
      string
    >({
      query: (id) => ({
        url: `/edi-conversion/${id}/reprocess-implant-associations`,
        method: "POST",
      }),
      invalidatesTags: ["EdiConversion"],
    }),
    reprocessCptCodeQuantity: builder.mutation<
      { data: IEdiConversionFile; message: string },
      string
    >({
      query: (id) => ({
        url: `/edi-conversion/${id}/reprocess-cpt-code-quantity`,
        method: "POST",
      }),
      invalidatesTags: ["EdiConversion"],
    }),
    runAiAnalysis: builder.mutation<
      { data: { claimCount: number }; message: string },
      string
    >({
      query: (id) => ({
        url: `/edi-conversion/${id}/run-ai-analysis`,
        method: "POST",
      }),
      invalidatesTags: ["EdiConversion"],
    }),
  }),
});

export const {
  useGetAllEdiConversionFilesQuery,
  useGetEdiConversionFileByIdQuery,
  useConvertAndUploadEdiMutation,
  useGetEdiConversionFileDownloadUrlQuery,
  useDeleteEdiConversionFileMutation,
  useReprocessEdiFileMutation,
  useReprocessCptCoOccurrenceMutation,
  useReprocessImplantAssociationsMutation,
  useReprocessCptCodeQuantityMutation,
  useRunAiAnalysisMutation,
} = ediConversionQueries;
